<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  getCompetenceReconciliation,
  saveCompetenceAliases,
  type ReconRequirement,
} from "@/api/client";

const queryClient = useQueryClient();
const { data, isPending } = useQuery({
  queryKey: ["competence-reconciliation"],
  queryFn: () => getCompetenceReconciliation(),
});

// Local editable copy of each requirement's held-name aliases, keyed by name.
const draft = reactive<Record<string, string[]>>({});
const seeded = ref(false);
const filter = ref<"unmatched" | "all">("unmatched");
const busy = ref(false);
const msg = ref<string | null>(null);
const freeText = reactive<Record<string, string>>({});

function seed(): void {
  if (seeded.value || !data.value) return;
  for (const r of data.value.requirements) draft[r.requirement] = [...r.held_names];
  seeded.value = true;
}

const requirements = computed<ReconRequirement[]>(() => {
  seed();
  const list = data.value?.requirements ?? [];
  return filter.value === "unmatched" ? list.filter((r) => !isMatched(r)) : list;
});

// A row is matched once it has at least one held-name alias in the draft.
function isMatched(r: ReconRequirement): boolean {
  return (draft[r.requirement]?.length ?? 0) > 0 || r.matched;
}
function aliasesFor(r: ReconRequirement): string[] {
  return draft[r.requirement] ?? [];
}
function addAlias(r: ReconRequirement, name: string): void {
  const n = name.trim();
  if (!n) return;
  const list = draft[r.requirement] ?? (draft[r.requirement] = []);
  if (!list.includes(n)) list.push(n);
}
function removeAlias(r: ReconRequirement, name: string): void {
  draft[r.requirement] = (draft[r.requirement] ?? []).filter((x) => x !== name);
}
function addFreeText(r: ReconRequirement): void {
  addAlias(r, freeText[r.requirement] ?? "");
  freeText[r.requirement] = "";
}

const matchedCount = computed(
  () => (data.value?.requirements ?? []).filter((r) => isMatched(r)).length,
);

async function save(): Promise<void> {
  busy.value = true;
  msg.value = null;
  try {
    const aliases = Object.entries(draft)
      .filter(([, names]) => names.length)
      .map(([requirement, held_names]) => ({ requirement, held_names }));
    await saveCompetenceAliases({ aliases, source: data.value?.source });
    await queryClient.invalidateQueries({ queryKey: ["competence-reconciliation"] });
    await queryClient.invalidateQueries({ queryKey: ["compliance-dashboard"] });
    msg.value = `Saved ${aliases.length} mapping(s).`;
  } catch (err) {
    msg.value = err instanceof Error ? err.message : String(err);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
    <div
      v-if="isPending"
      class="text-sm text-neutral-400"
    >
      Loading requirements…
    </div>
    <template v-else-if="data">
      <div class="mb-3 flex flex-wrap items-center gap-3">
        <p class="text-sm text-neutral-600">
          <span class="font-medium text-neutral-900">{{ matchedCount }}</span>
          of {{ data.requirements.length }} requirements mapped to a held
          competence. Unmapped requirements that nobody is tracked against don't
          block rostering — they show as “not tracked yet”.
        </p>
        <div class="ml-auto flex gap-1">
          <button
            type="button"
            class="rounded-md px-2.5 py-1 text-sm"
            :class="filter === 'unmatched'
              ? 'bg-brand-primary text-neutral-0'
              : 'border border-neutral-300 hover:bg-neutral-50'"
            @click="filter = 'unmatched'"
          >
            Needs mapping
          </button>
          <button
            type="button"
            class="rounded-md px-2.5 py-1 text-sm"
            :class="filter === 'all'
              ? 'bg-brand-primary text-neutral-0'
              : 'border border-neutral-300 hover:bg-neutral-50'"
            @click="filter = 'all'"
          >
            All
          </button>
        </div>
      </div>

      <p
        v-if="!requirements.length"
        class="py-3 text-sm text-neutral-400"
      >
        Nothing to map in this view.
      </p>

      <ul class="grid gap-2">
        <li
          v-for="r in requirements"
          :key="r.requirement"
          class="border-t border-neutral-50 py-2 first:border-0"
        >
          <div class="flex flex-wrap items-center gap-2">
            <span class="font-medium text-neutral-900">{{ r.requirement }}</span>
            <span class="text-xs text-neutral-400">{{ r.division || "—" }} · {{ r.source }}</span>
            <span
              v-if="isMatched(r)"
              class="rounded-full bg-semantic-success/10 px-2 py-0.5 text-xs text-semantic-success"
            >mapped</span>
            <span
              v-else-if="!r.tracked"
              class="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500"
              title="Nobody is tracked against this — it won't block rostering until mapped"
            >not tracked</span>
            <span
              v-else
              class="rounded-full bg-semantic-warning/10 px-2 py-0.5 text-xs text-semantic-warning"
            >needs mapping</span>
          </div>

          <!-- Current aliases -->
          <div
            v-if="aliasesFor(r).length"
            class="mt-1 flex flex-wrap gap-1"
          >
            <span
              v-for="name in aliasesFor(r)"
              :key="name"
              class="inline-flex items-center gap-1 rounded-full bg-brand-primary/10 px-2 py-0.5 text-xs text-brand-primary"
            >
              {{ name }}
              <button
                type="button"
                class="text-brand-primary hover:text-semantic-danger"
                @click="removeAlias(r, name)"
              >×</button>
            </span>
          </div>

          <!-- Suggested candidates -->
          <div
            v-if="r.candidates.length"
            class="mt-1 flex flex-wrap items-center gap-1"
          >
            <span class="text-xs text-neutral-400">Suggestions:</span>
            <button
              v-for="c in r.candidates"
              :key="c.name"
              type="button"
              class="rounded-full border border-neutral-300 px-2 py-0.5 text-xs text-neutral-600 hover:bg-neutral-50"
              :title="`token match ${c.score}`"
              @click="addAlias(r, c.name)"
            >
              + {{ c.name }}
            </button>
          </div>

          <!-- Free-text add -->
          <div class="mt-1 flex items-center gap-1">
            <input
              v-model="freeText[r.requirement]"
              placeholder="Map a held competence by name…"
              class="w-72 rounded-md border border-neutral-300 px-2 py-1 text-xs"
              @keyup.enter="addFreeText(r)"
            >
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-2 py-1 text-xs hover:bg-neutral-50"
              @click="addFreeText(r)"
            >
              Add
            </button>
          </div>
        </li>
      </ul>

      <div class="mt-3 flex items-center gap-3">
        <button
          type="button"
          class="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-neutral-0 hover:opacity-90"
          :disabled="busy"
          @click="save"
        >
          {{ busy ? "Saving…" : "Save mappings" }}
        </button>
        <span
          v-if="msg"
          class="text-sm text-neutral-500"
        >{{ msg }}</span>
      </div>
    </template>
  </div>
</template>
